import { MoleculeConverter } from '@moleculeone/molecule-utils';
import { setPositions } from '@moleculeone/draw';
import type { Mol, Reaction } from '@moleculeone/types';

import { calculateSeededPositions } from './mol';

declare const wasm_bindgen: any;

let moleculeConverter: any;

function getMoleculeConverter() {
  if (!moleculeConverter) {
    moleculeConverter = new MoleculeConverter({ ...wasm_bindgen });
  }

  return moleculeConverter;
}

function getErrorStack(error: unknown) {
  if (error instanceof Error) {
    return error.stack;
  }

  return String(error);
}

export async function smilesToMol(smiles: string, params = {}) {
  try {
    const mol = await getMoleculeConverter().smilesToMol(smiles, params);
    return setPositions(
      mol,
      calculateSeededPositions({
        mol,
        seedId: smiles,
      })
    );
  } catch (error) {
    console.error('Failed to convert SMILES to mol: %s', getErrorStack(error));
    throw error;
  }
}

export async function getReactionMapping(
  partialReaction: Pick<Reaction, 'product' | 'substrates'>
): Promise<Pick<Reaction, 'productMap' | 'substratesMap'>> {
  const { product, substrates } = partialReaction;

  const { product_to_map, substrates_to_map } = await wasm_bindgen.getReactionMapping(product, substrates.join('.'));

  return {
    productMap: product_to_map,
    substratesMap: substrates_to_map,
  };
}

export async function molfileToMol(molfile: string, params = {}) {
  try {
    const mol = await getMoleculeConverter().molfileToMol(molfile, params);
    return setPositions(
      mol,
      calculateSeededPositions({
        mol,
        seedId: molfile,
      })
    );
  } catch (error) {
    console.error('Failed to convert molfile to mol: %s', getErrorStack(error));
    throw error;
  }
}

export async function molToSmiles(mol: Mol) {
  try {
    return getMoleculeConverter().molToSmiles(mol);
  } catch (error) {
    console.error('Failed to convert mol to SMILES: %s', getErrorStack(error));
    throw error;
  }
}

export async function molToMolfile(mol: Mol) {
  try {
    return getMoleculeConverter().molToMolfile(mol);
  } catch (error) {
    console.error('Failed to convert mol to molfile: %s', getErrorStack(error));
    throw error;
  }
}

export async function reactionToRxn(options: { productSmiles: string; substrateSmiles: string[] }) {
  const { productSmiles, substrateSmiles = [] } = options;

  try {
    return getMoleculeConverter().reactionToRxn({ productSmiles, substrateSmiles });
  } catch (error) {
    console.error('Failed to convert reaction data to RXN: %s', getErrorStack(error));
    throw error;
  }
}
